import PropTypes from 'prop-types';
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { IntlProvider } from 'react-intl';

import { applyI18nConfig, loadI18nConfig, resolveLocale } from './handler';

const I18nContext = createContext(null);

/**
 * Provides the active application locale and a session-scoped update API.
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {object} props.initialConfig
 * @returns {React.ReactElement} locale providers.
 */
export function I18nProvider({ children, initialConfig }) {
  const [config, setConfig] = useState(initialConfig);
  const configRef = useRef(initialConfig);
  const desiredLocaleRef = useRef(initialConfig.fullLocale);
  const mountedRef = useRef(true);
  const pendingRequestRef = useRef(null);
  const requestGenerationRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestGenerationRef.current += 1;
      pendingRequestRef.current = null;
    };
  }, []);

  const updateLocaleFunc = useCallback((nextFullLocale) => {
    let resolution;
    try {
      resolution = resolveLocale(nextFullLocale);
    } catch (error) {
      return Promise.reject(error);
    }

    const desiredLocale = resolution.fullLocale;
    if (desiredLocaleRef.current !== desiredLocale) {
      desiredLocaleRef.current = desiredLocale;
      requestGenerationRef.current += 1;
      pendingRequestRef.current = null;
    }

    const requestGeneration = requestGenerationRef.current;
    const pendingRequest = pendingRequestRef.current;
    if (pendingRequest?.fullLocale === desiredLocale
      && pendingRequest.generation === requestGeneration) {
      return pendingRequest.promise;
    }
    if (configRef.current.fullLocale === desiredLocale) {
      return Promise.resolve(configRef.current);
    }

    const request = { fullLocale: desiredLocale, generation: requestGeneration, promise: null };
    const isRequestCurrent = () => mountedRef.current
      && requestGeneration === requestGenerationRef.current
      && desiredLocale === desiredLocaleRef.current;
    request.promise = loadI18nConfig(desiredLocale)
      .then((nextConfig) => {
        if (!isRequestCurrent()) {
          return null;
        }
        applyI18nConfig(nextConfig);
        configRef.current = nextConfig;
        setConfig(nextConfig);
        return nextConfig;
      }, (error) => {
        if (!isRequestCurrent()) {
          return null;
        }
        throw error;
      })
      .finally(() => {
        if (pendingRequestRef.current === request) {
          pendingRequestRef.current = null;
        }
      });
    pendingRequestRef.current = request;
    return request.promise;
  }, []);

  const contextValue = useMemo(() => ({
    ...config,
    updateLocaleFunc,
  }), [config, updateLocaleFunc]);

  return (
    <I18nContext.Provider value={contextValue}>
      <IntlProvider
        defaultLocale={config.defaultLocale}
        locale={config.locale}
        messages={config.messages}
      >
        {children}
      </IntlProvider>
    </I18nContext.Provider>
  );
}

I18nProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialConfig: PropTypes.shape({
    defaultLocale: PropTypes.string.isRequired,
    fullLocale: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    messages: PropTypes.objectOf(PropTypes.string).isRequired,
  }).isRequired,
};

/**
 * Returns the active i18n configuration and runtime locale updater.
 * @returns {object} i18n context value.
 */
export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};
